/**
 * StatisticsTab — Phase 15.
 *
 * Aggregations-Tabelle pro Lieferant (Huck/Helo/GlaWel/Sporttore/WFF) mit:
 *   - Total Auftraege im Zeitraum
 *   - Davon stuck (Notifications)
 *   - Davon puenktlich (done, ohne Notification)
 *   - Davon noch offen
 *   - Stuck-Rate %
 *   - Ø Lead-Time (Stunden bis closed_at)
 *
 * Zeitraum-Filter via Date-Inputs. Default: letzte 90 Tage.
 * Hinweis im UI: Daten ab 28.05.2026 (Re-Ingest).
 */

import { useEffect, useState, useCallback } from 'react';
import { Calendar, Info, RefreshCw } from 'lucide-react';
import { api } from '@libero/core/services/api';

interface StatRow {
  supplierKey: string;
  supplierName: string;
  total: number;
  stuck: number;
  onTime: number;
  open: number;
  avgLeadTimeHours: number | null;
}

interface StatisticsResponse {
  from: string | null;
  to: string | null;
  dataAvailableSince: string;
  rows: StatRow[];
}

function formatHoursReadable(h: number | null): string {
  if (h == null) return '–';
  if (h < 48) return `${Math.round(h)}h`;
  const days = Math.round(h / 24);
  if (days < 14) return `${days} Tage`;
  return `${Math.round(days / 7)} Wochen`;
}

function ninetyDaysAgo(): string {
  const d = new Date();
  d.setDate(d.getDate() - 90);
  return d.toISOString().slice(0, 10);
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function StatisticsTab() {
  const [from, setFrom] = useState<string>(ninetyDaysAgo());
  const [to, setTo] = useState<string>(todayISO());
  const [data, setData] = useState<StatisticsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/programmes/auftragsverfolgung/statistics?from=${from}&to=${to}`);
      setData(res.data?.data as StatisticsResponse);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? err?.message ?? 'Statistik konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-4 p-2">
      {/* Filter */}
      <div className="flex flex-wrap items-end gap-3 p-3 border border-[#DAD9D9] rounded">
        <div>
          <label className="block text-xs font-medium text-[#1A1A1A] mb-1">Von</label>
          <div className="flex items-center gap-1.5 border border-[#DAD9D9] rounded px-2">
            <Calendar className="w-4 h-4 text-gray-500" />
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="py-1.5 text-sm bg-transparent focus:outline-none"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-[#1A1A1A] mb-1">Bis</label>
          <div className="flex items-center gap-1.5 border border-[#DAD9D9] rounded px-2">
            <Calendar className="w-4 h-4 text-gray-500" />
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="py-1.5 text-sm bg-transparent focus:outline-none"
            />
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#E9FF57] text-[#1A1A1A] rounded text-sm font-medium hover:bg-[#d4ea4d] disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Aktualisieren
        </button>
      </div>

      {/* Hinweis */}
      <div className="flex items-start gap-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>
          <strong>Daten ab {data?.dataAvailableSince ?? '28.05.2026'}.</strong> Aeltere Auftraege haben keinen
          Status-Verlauf und sind in der Statistik nicht erfasst. Bei Doppelzaehlung (eine Order wurde mehreren
          Lieferanten zugeordnet) summieren sich die Werte auf mehr als die Gesamt-Order-Zahl.
        </div>
      </div>

      {/* Tabelle */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">{error}</div>
      )}
      {loading && <div className="text-sm text-gray-500">Lade Statistik…</div>}
      {data && !loading && (
        <div className="border border-[#DAD9D9] rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F5F5] text-[#1A1A1A]">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Lieferant</th>
                <th className="text-right px-3 py-2 font-semibold">Gesamt</th>
                <th className="text-right px-3 py-2 font-semibold">Stuck</th>
                <th className="text-right px-3 py-2 font-semibold">Puenktlich</th>
                <th className="text-right px-3 py-2 font-semibold">Noch offen</th>
                <th className="text-right px-3 py-2 font-semibold">Stuck-Rate</th>
                <th className="text-right px-3 py-2 font-semibold">Ø Lead-Time</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => {
                const stuckRate = r.total > 0 ? (r.stuck / r.total) * 100 : 0;
                const rateColor = stuckRate < 5 ? 'text-green-700' : stuckRate < 15 ? 'text-amber-700' : 'text-red-700';
                return (
                  <tr key={r.supplierKey} className="border-t border-[#DAD9D9]">
                    <td className="px-3 py-2 font-medium text-[#1A1A1A]">{r.supplierName}</td>
                    <td className="px-3 py-2 text-right">{r.total.toLocaleString('de-DE')}</td>
                    <td className="px-3 py-2 text-right text-red-700">{r.stuck.toLocaleString('de-DE')}</td>
                    <td className="px-3 py-2 text-right text-green-700">{r.onTime.toLocaleString('de-DE')}</td>
                    <td className="px-3 py-2 text-right text-gray-500">{r.open.toLocaleString('de-DE')}</td>
                    <td className={`px-3 py-2 text-right font-semibold ${rateColor}`}>
                      {stuckRate.toFixed(1)}%
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700">
                      {formatHoursReadable(r.avgLeadTimeHours)}
                    </td>
                  </tr>
                );
              })}
              {data.rows.every((r) => r.total === 0) && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-sm text-gray-500 italic">
                    Keine Daten im gewaehlten Zeitraum.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
